import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import type { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ControlledTextarea } from '#components/form/ControlledTextarea.tsx';
import { ControlledFormInput } from '#components/form/FormInput.tsx';
import { ControlledSelect } from '#components/form/FormSelect.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { Order } from '#domain/Order.ts';
import { type CapacityMetric, getRelevantCapacityMetrics } from '#domain/tourCapacity.ts';

import type { TourFormValues } from '../model/NewTourFormState.ts';
import { TourCapacityForm } from './TourCapacityForm.tsx';

interface TourFormProps {
    control: Control<TourFormValues>;
    driverOptions: SelectOption[];
    isTourStarted?: boolean;
    selectedOrders: Order[];
    exceededCapacityDimensions?: CapacityMetric[];
}

export const TourForm = ({
    control,
    driverOptions,
    isTourStarted = false,
    selectedOrders,
    exceededCapacityDimensions = [],
}: TourFormProps) => {
    const showDriver = useFeatureToggle('showDriver', false);

    const showCompactCapacity = getRelevantCapacityMetrics(selectedOrders).size > 0;
    const planningFieldColumns = showCompactCapacity
        ? showDriver
            ? 'grid-cols-4-sm'
            : 'grid-cols-3-sm'
        : showDriver
          ? 'grid-cols-3-sm'
          : 'grid-cols-2-sm max-width-900';
    const intl = useIntl();
    const tourName = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourName',
    });
    const tourDriver = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourDriver',
    });
    const noDriverFound = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.no-driver-found',
    });
    const selectDriverPlaceholder = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selectTourDriver',
    });

    return (
        <div className={`display-grid grid-cols-1 tour-planning-fields ${planningFieldColumns} gap-20`}>
            <ControlledFormInput label={tourName} name='tourNumber' control={control} />
            <ControlledTextarea
                name='note'
                control={control}
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.note',
                })}
                formGroupClassName='tour-comment'
                inputGroupClassName='margin-0'
                rows={1}
            />
            {showCompactCapacity && (
                <TourCapacityForm
                    control={control}
                    orders={selectedOrders}
                    compact
                    exceededDimensions={exceededCapacityDimensions}
                />
            )}
            {showDriver && (
                <ControlledSelect
                    control={control}
                    disabled={isTourStarted}
                    name='driver'
                    label={tourDriver}
                    options={driverOptions}
                    inputAddon='uikit-icon icon-driver'
                    placeholder={selectDriverPlaceholder}
                    useFilter
                    noItemMessage={noDriverFound}
                />
            )}
        </div>
    );
};
